// File menu, Add menu, context menus. Keyboard: ↑/↓ to move, Enter to run, Esc to close.
import * as barcode from '../itf/barcode.js';
import { S, select, field, isLocked } from './state.js';
import { $, h, kbd, escapeHtml } from './dom.js';
import { icon, FIELD_LETTER, FIELD_ICON_CLASS } from './icons.js';
import * as A from './actions.js';
import * as V from './view.js';
import { openDialog, newBlank, save, exportForPrinter } from './io.js';
import { exportPNG, printProof } from './export.js';

let open = null; // { el, anchor }

export function closeMenus() {
  if (!open) return;
  const { el, anchor } = open;
  open = null;
  el.remove();
  document.removeEventListener('mousedown', onOutside, true);
  if (anchor) anchor.setAttribute('aria-expanded', 'false');
}
function onOutside(e) {
  if (open && !open.el.contains(e.target) && !(open.anchor && open.anchor.contains(e.target))) closeMenus();
}

/**
 * items: { label, icon?, letter?(kind), kbd?, run, disabled?, title? } | { sep:true } | { section:'Text' }
 * at: { x, y } (fixed position) ; opts.anchor: element that opened it
 */
function showMenu(items, at, { anchor = null, cls = 'menu', label = 'Menu' } = {}) {
  const wasSame = open && anchor && open.anchor === anchor;
  closeMenus();
  if (wasSame) return;
  const el = h('div', { class: cls, role: 'menu', 'aria-label': label, style: { position: 'fixed', left: `${at.x}px`, top: `${at.y}px` } });
  for (const it of items) {
    if (it.sep) {
      el.appendChild(h('div', { class: 'menu-sep', role: 'separator' }));
      continue;
    }
    if (it.section) {
      el.appendChild(h('div', { class: 'menu-section', role: 'presentation' }, it.section));
      continue;
    }
    const lead = it.letter
      ? h('div', { class: `field-icon ${FIELD_ICON_CLASS[it.letter] || 'unknown'}`, style: { width: '18px', height: '18px', fontSize: '9px' } }, FIELD_LETTER[it.letter] || '·')
      : icon(it.icon || 'add');
    const item = h(
      'div',
      {
        class: 'menu-item',
        role: 'menuitem',
        tabindex: '-1',
        title: it.title || null,
        'aria-disabled': it.disabled ? 'true' : null,
        style: it.disabled ? { opacity: '0.4', cursor: 'default' } : null,
        onClick: () => {
          if (it.disabled) return;
          closeMenus();
          it.run();
        },
      },
      lead,
      h('span', null, it.label),
      it.kbd ? h('span', { class: 'kbd' }, it.kbd) : null,
    );
    el.appendChild(item);
  }
  el.addEventListener('keydown', (e) => {
    const list = [...el.querySelectorAll('.menu-item:not([aria-disabled])')];
    const i = list.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = list[(i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length];
      if (n) n.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (document.activeElement && list.includes(document.activeElement)) document.activeElement.click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeMenus();
      if (anchor) anchor.focus();
    } else if (e.key === 'Tab') {
      closeMenus();
    }
  });
  el.addEventListener('focusin', (e) => {
    for (const m of el.querySelectorAll('.menu-item')) m.classList.toggle('focused', m === e.target);
  });
  document.body.appendChild(el);
  const r = el.getBoundingClientRect();
  if (r.right > window.innerWidth - 8) el.style.left = `${Math.max(8, window.innerWidth - r.width - 8)}px`;
  if (r.bottom > window.innerHeight - 8) el.style.top = `${Math.max(8, window.innerHeight - r.height - 8)}px`;
  open = { el, anchor };
  if (anchor) anchor.setAttribute('aria-expanded', 'true');
  setTimeout(() => document.addEventListener('mousedown', onOutside, true), 0);
  const first = el.querySelector('.menu-item:not([aria-disabled])');
  if (first) first.focus({ preventScroll: true });
  return el;
}

const below = (anchor) => {
  const r = anchor.getBoundingClientRect();
  return { x: r.left, y: r.bottom + 4 };
};

export function openFileMenu(anchor = $('#btn-file')) {
  const has = !!S.label;
  showMenu(
    [
      { icon: 'open', label: 'Open .itf / .image…', kbd: kbd('Mod+O'), run: openDialog },
      { icon: 'new', label: 'New blank label', kbd: kbd('Mod+Alt+N'), run: newBlank },
      { icon: 'recent', label: 'Recent files…', run: () => import('./cmdk.js').then((m) => m.openCmdK('recent ')) },
      { sep: true },
      { icon: 'save', label: 'Save', kbd: kbd('Mod+S'), run: () => save('save'), disabled: !has },
      { icon: 'save', label: 'Save as…', kbd: kbd('Mod+Shift+S'), run: () => save('as'), disabled: !has },
      { icon: 'download', label: 'Download a copy', run: () => save('download'), disabled: !has },
      { icon: 'print', label: 'Export for printer (.image + .job)…', run: exportForPrinter, disabled: !has, title: 'The file pair the SmartDate X65 loads from USB' },
      { sep: true },
      { icon: 'png', label: 'Export PNG proof (305 dpi)', run: exportPNG, disabled: !has },
      { icon: 'print', label: 'Print proof (PDF)…', run: printProof, disabled: !has },
    ],
    below(anchor),
    { anchor, label: 'File' },
  );
}

function symbologyItems(at) {
  const list = Array.isArray(barcode.SYMBOLOGIES) ? barcode.SYMBOLOGIES : [];
  return list.map((sy) => ({ letter: 'barcode', label: sy.label || sy.id, run: () => A.addFieldOfKind('barcode', { ...(at || {}), symbology: sy.id }) }));
}

export function addMenuItems(at = null) {
  const sym = symbologyItems(at);
  return [
    { section: 'Text' },
    { letter: 'text', label: 'Text', kbd: 'T', run: () => A.addFieldOfKind('text', at || undefined) },
    { section: 'Date' },
    { letter: 'date', label: 'Date', kbd: 'D', run: () => A.addFieldOfKind('date', at || undefined) },
    { section: 'Barcode' },
    ...(sym.length ? sym : [{ letter: 'barcode', label: 'Barcode', kbd: 'B', run: () => A.addFieldOfKind('barcode', at || undefined) }]),
  ];
}

export function openAddMenu(anchor) {
  if (!S.label) return;
  showMenu(addMenuItems(), below(anchor), { anchor, label: 'Add field' });
}

export function fieldContextItems(f) {
  if (!f) return [];
  if (f.kind === 'unsupported') {
    return [
      { icon: 'lock', label: 'Unsupported field — kept unchanged', run: () => {}, disabled: true },
      { sep: true },
      { icon: 'front', label: 'Bring to front', kbd: kbd('Mod+Shift+]'), run: A.bringToFront },
      { icon: 'back', label: 'Send to back', kbd: kbd('Mod+Shift+['), run: A.sendToBack },
    ];
  }
  const locked = isLocked(f);
  return [
    { icon: 'dup', label: 'Duplicate', kbd: kbd('Mod+D'), run: A.duplicateSelected },
    { icon: 'rotate', label: 'Rotate 90°', kbd: 'R', run: () => A.rotateSelected(90), disabled: locked },
    { icon: locked ? 'unlock' : 'lock', label: locked ? 'Unlock' : 'Lock', kbd: kbd('Mod+L'), run: A.toggleLockSelected, title: 'Editor only — not saved to the file' },
    { sep: true },
    { icon: 'front', label: 'Bring to front', kbd: kbd('Mod+Shift+]'), run: A.bringToFront },
    { icon: 'back', label: 'Send to back', kbd: kbd('Mod+Shift+['), run: A.sendToBack },
    { sep: true },
    { icon: 'del', label: 'Delete', kbd: 'Del', run: A.deleteSelected },
  ];
}

export function openFieldContextMenu(x, y, key) {
  const f = field(key);
  if (!f) return;
  if (!S.selection.includes(key)) select([key]);
  showMenu(fieldContextItems(f), { x, y }, { cls: 'ctxmenu', label: `${escapeHtml(f.name)} actions` });
}

/** at = label units where the user right-clicked */
export function openCanvasContextMenu(x, y, at) {
  if (!S.label) return;
  const pos = at ? { x: Math.max(0, Math.round(at.x)), y: Math.max(0, Math.round(at.y)) } : null;
  showMenu(
    [
      { letter: 'text', label: 'Add text here', run: () => A.addFieldOfKind('text', pos) },
      { letter: 'date', label: 'Add date here', run: () => A.addFieldOfKind('date', pos) },
      { letter: 'barcode', label: 'Add barcode here', run: () => A.addFieldOfKind('barcode', pos) },
      { sep: true },
      { icon: 'field', label: 'Select all', kbd: kbd('Mod+A'), run: A.selectAll, disabled: !S.fields.length },
      { icon: 'fit', label: 'Fit to view', kbd: 'F', run: V.fitToView },
      { icon: 'fit', label: 'Actual size (1:1)', kbd: '1', run: V.actualSize },
    ],
    { x, y },
    { cls: 'ctxmenu', label: 'Canvas actions' },
  );
}

export function openZoomMenu(anchor = $('#btn-zoom-readout')) {
  const has = !!S.label;
  showMenu(
    [
      { icon: 'add', label: 'Zoom in', kbd: kbd('Mod+='), run: V.zoomIn, disabled: !has },
      { icon: 'fit', label: 'Zoom out', kbd: kbd('Mod+-'), run: V.zoomOut, disabled: !has },
      { sep: true },
      { icon: 'fit', label: 'Fit to view', kbd: 'F', run: V.fitToView, disabled: !has },
      { icon: 'ruler', label: 'Actual size (1:1)', kbd: '1', run: V.actualSize, disabled: !has },
      { sep: true },
      ...[50, 100, 200, 400].map((p) => ({ icon: 'grid', label: `${p}%`, run: () => V.setZoom(p / 100), disabled: !has })),
      { sep: true },
      { icon: 'ruler', label: 'Calibrate screen size…', run: V.openDPICalibration },
    ],
    below(anchor),
    { anchor, label: 'Zoom' },
  );
}

export function initMenus() {
  $('#btn-zoom-readout').addEventListener('click', (e) => openZoomMenu(e.currentTarget));
  $('#btn-file').addEventListener('click', (e) => openFileMenu(e.currentTarget));
  $('#btn-add-field').addEventListener('click', (e) => openAddMenu(e.currentTarget));
  $('#tool-palette').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tool]');
    if (!btn) return;
    const tool = btn.dataset.tool;
    if (tool === 'add-text') A.addFieldOfKind('text');
    else if (tool === 'add-date') A.addFieldOfKind('date');
    else if (tool === 'add-barcode') {
      if (!S.label) A.addFieldOfKind('barcode');
      else {
        const items = symbologyItems(null);
        if (items.length) showMenu([{ section: 'Barcode' }, ...items], below(btn), { anchor: btn, label: 'Add barcode' });
        else A.addFieldOfKind('barcode');
      }
    }
  });
}
